import 'regenerator-runtime/runtime';
import { call, cancel, select, take, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  getInfosFromDeltafaktoren,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  isAngebotsIdEqual,
  QuoteDbInitPayload,
  QuoteDbRequestPayload,
  QuoteDbSpartePayload,
} from '@ps20-frontend/shop-integration-redux';
import { App, getTarifbereich, Produktlinie } from '@ps20-frontend/frontend-models';
import {
  buildTaasRequestModelForShop,
  buildTaasRequestModelForStrecke,
  createHashedTaasData,
  getSelectedProduktlinie,
  getTaasVertragsoptionen,
  TaasData,
} from '@ps20-frontend/preis-lib';
import { Dialogschritt, TaasRequestModel, TaasVhvV2Data, VorschadenArt } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { VhvActions } from '../vhvActions';
import { VorvertragVorschaedenVhv } from '../reducers/transient-state.reducer';
import { IAppState } from '../appState';
import {
  buildTaasVHVDataForShop,
  buildTaasVHVDataForStrecke,
} from './transformer/vhvData.transformer';

export function* watchQuoteDB() {
  yield takeLatest(GlobalActions.QUOTEDB_DATA_REQUESTED, safeSaga(getQuoteDbData, App.VHV_V2));
  yield takeLatest(VhvActions.QUOTEDB_REQUESTED_VHV, safeSaga(quoteDbInit, App.VHV_V2));
}

export function* getQuoteDbData(action: GlobalAction<QuoteDbRequestPayload>) {
  const angebotsIds: AngebotsId[] = action.payload.angebotsIds;
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  let taasRequestModel: TaasRequestModel<TaasVhvV2Data>;
  let selectedProduktlinie: Produktlinie;
  let alleProduktlinien: Array<Produktlinie>;
  const spartenAngebotsId: AngebotsId = {
    appId: App.VHV_V2,
    angebotsId: state.angebotsId,
  };
  if (angebotsIds && !angebotsIds.some(angebot => isAngebotsIdEqual(spartenAngebotsId, angebot))) {
    yield cancel();
    return;
  }

  if (
    (state.produkt && state.gesamtbeitrag !== 0) ||
    state.transientState.fehlerCodeTaas ||
    (state.vertragsoptionen?.beibehalten && state.gesamtbeitragMitRabatt !== 0)
  ) {
    selectedProduktlinie = getSelectedProduktlinie(state.produkt);
    alleProduktlinien = state.produkt.produktlinien;
    if (action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN) {
      taasRequestModel = yield call(buildTaasRequestModel, state);
    } else {
      taasRequestModel = yield call(buildTaasRequestModelShop, state);
    }
    taasRequestModel = addVorschaedenVorvertragToTaasRequestModel(
      taasRequestModel,
      state.transientState.vorvertragVorschaeden,
    );
  }

  const quoteDbPayload: QuoteDbSpartePayload<TaasData> = {
    angebotsId: spartenAngebotsId,
    taasSparteRequestModel: taasRequestModel,
    selectedProduktlinie,
    alleProduktlinien,
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    gesamtpreis:
      action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN
        ? state.gesamtbeitrag
        : state.gesamtbeitragMitRabatt,
    vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
    antragsnummer: state.antragsnummer,
  };

  getInfosFromDeltafaktoren(state.taasInfo, state.vertragsoptionen, quoteDbPayload);

  yield call(globalPut.put, new GlobalActions().quoteDBDataSparteGenerated(quoteDbPayload));
}

export function* quoteDbInit() {
  const globalPut = GlobalPut.getInstance();
  yield take(GlobalActions.GESAMTPREIS_CHANGED);
  const state: IAppState = yield select();
  const fehlerCodeTaas = state.transientState.fehlerCodeTaas;
  if (fehlerCodeTaas === null) {
    yield cancel();
    return;
  }
  const quoteDbInitPayload: QuoteDbInitPayload = {
    ausloeserAngebotsId: { angebotsId: state.angebotsId, appId: App.VHV_V2 },
    dialogschritt: Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN,
    fehlerCodeTaas,
  };
  yield call([globalPut, globalPut.put], new GlobalActions().quoteDBInit(quoteDbInitPayload));
}

export function buildTaasRequestModel(state: IAppState): TaasRequestModel<TaasVhvV2Data> {
  const taasData: TaasVhvV2Data = buildTaasVHVDataForStrecke(state);
  return buildTaasRequestModelForStrecke(
    getTaasVertragsoptionen(state.vertragsoptionen),
    taasData,
    getTarifbereich(state.versicherungsnehmer),
  );
}

export function buildTaasRequestModelShop(state: IAppState): TaasRequestModel<TaasVhvV2Data> {
  const vhvData: TaasVhvV2Data = buildTaasVHVDataForShop(state);
  const newHashedTaasRequest = createHashedTaasData(vhvData);
  return buildTaasRequestModelForShop(
    vhvData,
    newHashedTaasRequest,
    getTaasVertragsoptionen(state.vertragsoptionen),
    state.taasInfo,
    getTarifbereich(state.versicherungsnehmer),
  );
}

export function addVorschaedenVorvertragToTaasRequestModel(
  taasRequestModel: TaasRequestModel<TaasVhvV2Data>,
  vorvertragVorschaeden: VorvertragVorschaedenVhv,
): TaasRequestModel<TaasVhvV2Data> {
  if (vorvertragVorschaeden != null) {
    return {
      ...taasRequestModel,
      taasSparte: {
        ...taasRequestModel.taasSparte,
        vorschaden: taasRequestModel.taasSparte.vorschaden.map(vorschaden => {
          const manuallyEntered = vorschaden.manuallyEntered ?? false;
          return {
            vorschadenArt: vorschaden.vorschadenArt,
            anzahl: manuallyEntered
              ? vorschaden.anzahl
              : vorvertragVorschaeden[getVorschadenKey(vorschaden.vorschadenArt)] || 0,
            manuallyEntered,
          };
        }),
      },
    };
  }

  return taasRequestModel;
}

function getVorschadenKey(stateKey: VorschadenArt): string {
  const vorschadenMap = {
    [VorschadenArt.FELS]: 'fels',
    [VorschadenArt.RAD]: 'fahrrad',
    [VorschadenArt.ELEMENTAR]: 'elementar',
  };
  return (vorschadenMap as Record<string, string>)[stateKey];
}
